/**
 * Singleton composable for the "how do you want your PDF?" dialog.
 * Module-level state ensures the same dialog instance is shared across all components.
 *
 * Free users always have a free, watermarked download. The dialog lets them pick
 * between that and a clean copy (spending a credit they own, or buying one / Premium).
 */
export type ExportChoice =
  /** Spend one export credit, get a clean PDF. */
  | 'clean'
  /** Free download with watermark. */
  | 'watermark'
  /** User wants to buy a credit / Premium: caller opens the checkout. */
  | 'buy'
  | 'cancel'

const showDialog = ref(false)
const creditCount = ref(0)
let resolvePromise: ((choice: ExportChoice) => void) | null = null

export function useExportCreditConfirm() {
  /**
   * Shows the dialog and resolves with the user's choice.
   * `currentCredits` decides whether "use credit" or "buy" is offered.
   */
  function requestExportChoice(currentCredits: number): Promise<ExportChoice> {
    creditCount.value = currentCredits
    showDialog.value = true
    return new Promise((resolve) => {
      resolvePromise = resolve
    })
  }

  /**
   * @deprecated Use requestExportChoice(). Kept so older callers keep compiling:
   * resolves true only when the user chose to spend a credit.
   */
  async function requestConfirm(currentCredits: number): Promise<boolean> {
    return (await requestExportChoice(currentCredits)) === 'clean'
  }

  function choose(choice: ExportChoice) {
    showDialog.value = false
    resolvePromise?.(choice)
    resolvePromise = null
  }

  return {
    showDialog: readonly(showDialog),
    creditCount: readonly(creditCount),
    requestExportChoice,
    requestConfirm,
    choose,
    confirm: () => choose('clean'),
    cancel: () => choose('cancel'),
  }
}
